/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import Checkbox from '@mui/material/Checkbox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Divider,
  Stack,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  TextField,
  InputLabel,
  Typography,
  Grid,
  Paper,
  Box,
  FormHelperText,
  Tooltip,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TimerTwoToneIcon from '@mui/icons-material/TimerTwoTone';
import Close from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { styled } from '@mui/system';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { Form, FormikProvider, useFormik } from 'formik';
import { getProjectById } from '../../../../../services/projectService';
import { getAllUsers } from '../../../../../services/teamService';
import { getSectionsByProjectId } from '../../../../../services/sectionService';
import { createTask, getAllTasksByProjectId, updateTask } from '../../../../../services/taskService';
import { getTaskClassificationList } from '../../../../../services/taskClassification';
import { isAdmin, isClient, isStakeholder } from '../../../../../utils/share';
import { useProject } from '../../../Layout/header/context';
import SectionFormModal from './SectionFormModal';
import { AddMemberModal } from '..';
import { calculateEndDate } from '../../../../../utils/calculateEndDate';
import AttachmentUploader from './AttachmentUploader';
import TaskComments from './TaskComments';
// import { ROLE_CAN_EDIT_TASK_DETAILS_DATE } from '../../../../../Constants/ProjectConstants';

const INITIAL_TASK_FORM_STATE = (showProjectSelection = false) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return {
    taskName: '',
    userId: '',
    sectionId: '',
    classificationId: '',
    endDate: tomorrow,
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    description: '',
    status: 'Not Started',
    plannedHours: undefined,
    parentTaskId: undefined,
    taskType: 'Task',
    issueType: '',
    isBillable: true,
    priority: 1,
    isParentTask: false,
    ...(showProjectSelection && { projectId: '' }),
  };
};

function ProjectAddTask({
  hideProjectName,
  onAddTask,
  selectedTask,
  project,
  projectName,
  sectionId,
  classificationId,
  close,
  action,
  onSubmit,
  projects = [],
  canAddParentTask = false,
  showProjectSelection = false,
}) {
  // console.log('selectedTask', selectedTask);
  const { id } = useParams();
  const accountData = useSelector((state) => state?.login?.user || null);
  const userId = accountData?.id;
  const [employeeList, setEmployeeList] = useState([]);
  const [parentTask, setParentTask] = useState([]);
  const [taskName, setTaskName] = useState('');

  // const [filteredParentTask, setFilteredParentTask] = useState([]);
  const [sectionList, setSectionList] = useState([{}]);
  const [taskClassificationOption, setTaskClassificationOption] = useState([]);
  const [addSectionModal, setAddSectionModal] = useState(false);
  const [addMemberModal, setAddMemberModal] = useState(false);
  const [projectDetails, setProjectDetails] = useState({});
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const { projectDetails: projectDetailsContext, isEditPermission } = useProject();
  const navigate = useNavigate();
  // const location = useLocation();
  const classes = useStyles();
  const defaultFormData = useRef(INITIAL_TASK_FORM_STATE(showProjectSelection));

  const projectId = showProjectSelection
    ? selectedProjectId
    : selectedTask?.projectId || project?.id || id || projectDetailsContext?.id;
  const stakeholder = isStakeholder() || isClient();

  // const isTaskDateChangePermission = (() => {
  //   // Find the project member with the specified userId
  //   const projectMember = projectDetails?.projectmember?.find(
  //     (member) => member?.userId === userId
  //   );

  //   let hasRequiredRole = false;

  //   // Check if projectRoles is an array
  //   if (Array.isArray(projectMember?.projectRoles)) {
  //     hasRequiredRole = projectMember.projectRoles.some(
  //       (role) => ROLE_CAN_EDIT_TASK_DETAILS_DATE.includes(role.roleName)
  //     );
  //   }
  //   // Check if projectRoles is an object
  //   else if (projectMember?.projectRoles && typeof projectMember.projectRoles === 'object') {
  //     hasRequiredRole = Object.values(projectMember.projectRoles).some(
  //       (role) => ROLE_CAN_EDIT_TASK_DETAILS_DATE.includes(role.roleName)
  //     );
  //   }

  //   // Return true if the project member has any of the required roles or if the project allows manual tasks
  //   return hasRequiredRole || (projectDetails?.allowManualTask ?? false);
  // })();

  const handleClose = () => {
    setValues(INITIAL_TASK_FORM_STATE(showProjectSelection));
    setTouched({});
    setSelectedProjectId(null);
    close();
  };

  const createTaskSchema = Yup.object({
    taskName: Yup.string()
      .transform((value) => value?.trim())
      .required(stakeholder ? 'Issue Name is required' : 'Task Name is required')
      .min(2, 'Too Short'),
    userId: Yup.mixed().when('isParentTask', {
      is: true,
      then: (schema) => schema.nullable(true),
      otherwise: (schema) =>
        schema.required('Employee is required').test('is-valid-user', 'Employee is required', (value) => {
          return value !== null && value !== undefined && value !== '';
        }),
    }),
    sectionId: Yup.number().required('Section is required'),
    classificationId: Yup.number().nullable(),
    startDate: Yup.date().required('Start date is required').typeError('Enter valid start date'),
    endDate: Yup.date()
      .min(Yup.ref('startDate'), "End date can't be before start date")
      .required('End date is required')
      .typeError('Enter valid end date'),
    // description: Yup.string().required("Description is required"),
    plannedHours: Yup.mixed().when('taskType', {
      is: 'Issue',
      then: Yup.number()
        .nullable(true)
        .typeError('Planned Hours must be a number')
        .min(0, 'Please enter valid Hours')
        .notRequired(),
      otherwise: Yup.number()
        .required('Planned Hours is required')
        .typeError('Planned Hours must be a number')
        .min(0.1, 'Please enter valid Hours'),
    }),
    status: Yup.string().required('Status is required'),
    taskType: Yup.string()
      .required('Task Type is required')
      .test('no-issue-for-parent', 'Parent tasks cannot be Issue type', function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { isParentTask } = this.parent;
        if (isParentTask && value === 'Issue') {
          // eslint-disable-next-line react/no-this-in-sfc
          return this.createError({ message: 'Parent tasks cannot be Issue type' });
        }
        return true;
      }),
    parentTaskId: Yup.mixed().when(['taskType'], {
      is: (taskType) => taskType === 'Issue' && !isStakeholder() && !isClient(), // ONLY validate if NOT stakeholder
      then: (schema) =>
        schema
          .test('is-valid-parent', 'Parent Task is required', (value) => {
            if (value === null || value === undefined || value === '') return false;
            const num = Number(value);
            return Number.isFinite(num) && num > 0;
          })
          .required('Parent Task is required'),
      otherwise: (schema) => schema.nullable(true),
    }),
    issueType: Yup.string().when('taskType', {
      is: 'Issue',
      then: Yup.string().required('Issue Priority is required').nullable("Issue Priority can't be empty"),
      otherwise: Yup.string().nullable(),
    }),
    isParentTask: Yup.boolean(),
    isBillable: Yup.boolean().required('Please select if the task is billable'),
    priority: Yup.number().required('Priority is required').min(1, 'Priority atleast 1'),
    ...(showProjectSelection && { projectId: Yup.number().required('Project is required') }),
  });

  const formik = useFormik({
    initialValues: defaultFormData.current,
    validationSchema: createTaskSchema,
    onSubmit: (values) => {
      handleAPICall(values);
    },
  });

  const {
    touched,
    handleSubmit,
    setValues,
    submitForm,
    errors,
    isSubmitting,
    setSubmitting,
    getFieldProps,
    setTouched,
    setFieldValue,
    values,
  } = formik;

  // Ensure stakeholder defaults in form state (UI) without requiring backend
  useEffect(() => {
    if (!projectId) return;
    if (!stakeholder) return;
    // Set enforced defaults for stakeholders
    const next = { ...values };
    next.taskType = 'Issue';
    next.status = 'Not Started';
    next.issueType = next.issueType || 'Low';
    if (!next.plannedHours) next.plannedHours = 2;
    next.isBillable = false;
    next.classificationId = '';
    // Auto-select the first section for stakeholders (no section picker shown)
    const issuesSection = sectionList?.find((s) => (s?.name || '').toLowerCase().includes('issues'));
    const firstSection = sectionList?.[0];
    const autoSection = issuesSection || firstSection;
    if (autoSection?.id) next.sectionId = autoSection.id;
    setValues(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeholder, projectId, sectionList]);

  // const filterTask = (value) => {
  //   const filtered = parentTask.filter((option) => {
  //     if (!option || !option.taskName) return false;
  //     return option.taskName.toLowerCase().includes(value.toLowerCase().trim());
  //   });
  //   console.log('filtered', filtered);
  //   setFilteredParentTask(filtered);
  // };
  // const filterTask = (value) => {
  //   console.log('Filtering for:', value);
  //   const searchTerm = value.toLowerCase().trim();
  //   const filtered = parentTask.filter((option) => {
  //     if (!option || !option.taskName) return false;
  //     const isMatch = option.taskName.toLowerCase().includes(searchTerm);
  //     console.log(`Option: ${option.taskName}, Match: ${isMatch}`);
  //     return isMatch;
  //   });
  //   console.log('Filtered results:', filtered);
  //   setFilteredParentTask(filtered);
  // };

  // useEffect(() => {
  // const filtered = parentTask.filter((option) => {
  //   if (!option || !option.taskName) return false;
  //   return option.taskName.toLowerCase().includes(inputValue.toLowerCase().trim());
  // });
  // console.log('filtered', filtered);
  // setFilteredParentTask(filtered);
  // }, [parentTask]);

  useEffect(() => {
    if (values.taskType !== 'Issue') {
      setFieldValue('parentTaskId', null);
      setFieldValue('issueType', null);
    }
  }, [values.taskType, setFieldValue]);
  useEffect(() => {
    if ((isStakeholder() || isClient()) && action === 'create') {
      setFieldValue('userId', accountData?.id);
    }
  }, [action, accountData?.id, setFieldValue]);
  useEffect(() => {
    if (values.isParentTask) {
      setFieldValue('userId', null);
      // Parent tasks cannot be Issue type, so change to Task if it's Issue
      if (values.taskType === 'Issue') {
        setFieldValue('taskType', 'Task');
        setFieldValue('parentTaskId', null);
        setFieldValue('issueType', null);
      }
    }
  }, [values.isParentTask, setFieldValue, values.taskType]);

  const handleUpdate = () => {
    if (!selectedTask) {
      return;
    }
    defaultFormData.current = {
      taskName: selectedTask.taskName,
      plannedHours: selectedTask.plannedHours,
      parentTaskId: selectedTask.parentTaskId,
      type: selectedTask.type,
      userId: selectedTask.userId,
      sectionId: selectedTask.sectionId,
      classificationId: selectedTask.classificationId ?? '',
      startDate: selectedTask.startDate,
      endDate: selectedTask.endDate,
      description: selectedTask.description,
      status: selectedTask.status,
      issueType: selectedTask.issueType,
      priority: selectedTask.priority,
      isBillable: selectedTask.isBillable,
      isParentTask: selectedTask.isParentTask,
    };

    const formattedStartDate = selectedTask.startDate ? dayjs(selectedTask.startDate).format('YYYY-MM-DD') : null;
    const formattedEndDate = selectedTask.endDate ? dayjs(selectedTask.endDate).format('YYYY-MM-DD') : null;

    setValues({
      taskName: selectedTask.taskName,
      plannedHours: selectedTask.plannedHours,
      parentTaskId: selectedTask.parentTaskId,
      taskType: selectedTask.taskType,
      userId: selectedTask.isParentTask ? null : selectedTask.userId,
      sectionId: selectedTask.sectionId,
      classificationId: selectedTask.classificationId,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      description: selectedTask.description,
      status: selectedTask.status,
      priority: selectedTask.priority,
      issueType: selectedTask.issueType,
      isBillable: selectedTask.isBillable,
      isParentTask: selectedTask.isParentTask,
    });
  };
  useEffect(() => {
    if (selectedTask) {
      setAttachments(
        selectedTask.attachments.map((a) => ({
          key: a.path,
          name: a.path.split('/').pop(), // extract filename
        }))
      );
    }
  }, [selectedTask]);

  const cleanupPayload = (payload) => {
    const cleanedPayload = {};
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== '' && (value !== null || key === 'parentTaskId' || key === 'userId')) {
        cleanedPayload[key] = value;
      }
    });
    return cleanedPayload;
  };

  const handleAPICall = async (values) => {
    const cleanedValues = cleanupPayload(values);

    cleanedValues.attachments = attachments.map((a) => ({ path: a.key }));
    // Check if projectId is valid
    if (!projectId) {
      toast.error(stakeholder ? 'Please select a project before creating the issue' : 'Please select a project before creating the task');
      setSubmitting(false);
      return;
    }

    try {
      let Task;
      if (action === 'create') {
        // Enforce stakeholder constraints on payload
        if (stakeholder) {
          cleanedValues.taskType = 'Issue';
          cleanedValues.status = 'Not Started';
          cleanedValues.isBillable = false;
          cleanedValues.plannedHours = 2;
          // Remove classification for stakeholders
          delete cleanedValues.classificationId;
          // Auto-select Issues section if present, otherwise use the first section
          const issuesSection = sectionList?.find((s) => (s?.name || '').toLowerCase().includes('issues'));
          const autoSection = issuesSection || sectionList?.[0];
          if (autoSection?.id) cleanedValues.sectionId = autoSection.id;

          // Create/find parent "Not Aware" task for stakeholders
          try {
            // 1) Find "To Do" section id for this project
            const sectionsRes = await getSectionsByProjectId(projectId, { pageNumber: 0 });
            const allSections = sectionsRes?.data?.data?.rows || [];
            const toDoSection = allSections.find((s) => (s?.name || '').toLowerCase() === 'to do');
            if (!toDoSection) {
              throw new Error("'To Do' section not found in this project. Please create it first.");
            }

            // 2) Try to find existing parent task named exactly "Not Aware" within this project
            let parentTaskId;
            try {
              const listRes = await getAllTasksByProjectId({
                projectId,
                pageSize: 9999,
                filters: JSON.stringify([]),
                keyword: 'Not Aware',
                sorting: JSON.stringify([]),
              });
              const projectRows = listRes?.data?.rows || listRes?.rows || [];
              const exactMatch = projectRows.find((t) => (t?.taskName || '').trim().toLowerCase() === 'not aware');
              if (exactMatch) {
                parentTaskId = exactMatch.id;
              }
            } catch (_) {
              // ignore search errors; we'll create if not found
            }

            // 3) Create parent if not found
            if (!parentTaskId) {
              const today = dayjs();
              const tomorrow = dayjs().add(1, 'day');
              const parentPayload = {
                taskName: 'Not Aware',
                userId: accountData?.id,
                sectionId: toDoSection.id,
                endDate: tomorrow.format('YYYY-MM-DD'),
                startDate: today.format('YYYY-MM-DD'),
                status: 'Not Started',
                projectId,
                plannedHours: 1,
                isBillable: false,
                taskType: 'Task',
              };
              try {
                const parentRes = await createTask(parentPayload);
                parentTaskId = parentRes?.data?.data?.id || parentRes?.data?.id;
              } catch (createErr) {
                // If creation failed (possibly due to existing), re-query and pick existing
                const recheck = await getAllTasksByProjectId({
                  projectId,
                  pageSize: 9999,
                  filters: JSON.stringify([]),
                  globalFilter: 'Not Aware',
                  sorting: JSON.stringify([]),
                });
                const rows = recheck?.data?.rows || recheck?.rows || [];
                const existing = rows.find((t) => (t?.taskName || '').trim().toLowerCase() === 'not aware');
                if (existing) parentTaskId = existing.id;
              }
              if (!parentTaskId) throw new Error('Failed to create/find parent task "Not Aware"');
            }

            // 4) Set parent task ID for the issue
            cleanedValues.parentTaskId = parentTaskId;
            setFieldValue('parentTaskId', parentTaskId);
          } catch (parentError) {
            console.error('Parent task creation error:', parentError);
            toast.error(parentError.message || 'Failed to create parent task');
            setSubmitting(false);
            return;
          }
        }
        // Remove the parentTask field
        delete cleanedValues.parentTask;
        Task = await createTask({ ...cleanedValues, projectId });
        if (onAddTask) onAddTask(Task.data);
      } else if (action === 'update') {
        Task = await updateTask(selectedTask.id, { ...cleanedValues, projectId });
      }
      toast.success(stakeholder ? 'Issue saved successfully' : Task?.data?.message);

      if (onSubmit) onSubmit();
    } catch (error) {
      console.error('Task creation error:', error);
      toast.error(error?.response?.data?.message ?? error.message);
    } finally {
      setSubmitting(false);
      handleClose();
    }
  };

  const handleNavigate = () => {
    if (selectedTask.userId !== userId) {
      toast.info(`This is not your task`);
    } else if (
      selectedTask.status === 'To Do' ||
      selectedTask.status === 'In Progress' ||
      selectedTask.status === 'Not Started' ||
      selectedTask.status === 'Reopened'
    ) {
      navigate('/timetracker', {
        state: {
          taskName: selectedTask.taskName,
          projectId: selectedTask.projectId,
          id: selectedTask.id,
          userId: selectedTask.userId,
        },
      });
    } else if (selectedTask.status === 'Completed' || selectedTask.status === 'Blocked') {
      toast.info(`Task status is ${selectedTask.status}.`);
    }
    close();
  };

  const fetchEmployee = async () => {
    try {
      const Projects = await getProjectById(projectId);
      const membersList = Projects?.data?.data?.projectmember.map((member) => member?.userId);
      const employees = await getAllUsers();
      const filteredEmployees = employees?.data?.data
        .filter((emp) => membersList.includes(emp.id))
        .sort((a, b) => a.userName.localeCompare(b.userName));
      setEmployeeList(filteredEmployees);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hanldeAddSectionClick = () => {
    setAddSectionModal(true);
  };

  const handleCloseAddSection = () => {
    setAddSectionModal(false);
  };

  const hanldeAddMemberClick = () => {
    setAddMemberModal(true);
  };

  const handleCloseAddMember = () => {
    setAddMemberModal(false);
  };

  const onAddSection = (section) => {
    setSectionList((prev) => [...prev, section]);
    setFieldValue('sectionId', section?.id);
    setAddSectionModal(false);
  };

  const onAddMember = async () => {
    // member is coming as an argument
    await fetchEmployee();
    // setFieldValue('userId', member?.id);
    setAddMemberModal(false);
  };

  const fetchAllProjectTasks = async () => {
    try {
      const taskListResponse = await getAllTasksByProjectId({
        projectId,
        pageSize: 5,
        wantCompletedTask: true,
        globalFilter: taskName,
        forParent: true,
      });
      const tasks = (taskListResponse?.data?.rows || []).filter((task) => task.id !== selectedTask?.id);

      setParentTask(tasks);
      //  if (selectedTask?.parentTask?.id !== null && selectedTask?.parentTask?.id !== undefined) {
      //    // Check if parent task already exists based on id
      //    const parentTaskExists = tasks.some((task) => task.id === selectedTask?.parentTask.id);

      //    // Only add parent task if it doesn't already exist
      //    const newParentTasks = parentTaskExists ? tasks : [...tasks, selectedTask?.parentTask];
      //    setParentTask(newParentTasks);
      //  } else {
      //    const filteredParentTask = tasks.filter((task) => task.isParentTask);
      //    setParentTask(filteredParentTask);
      //  }
    } catch (error) {
      toast.error(error.message);
    }
  };
  useEffect(() => {
    if (sectionId) setFieldValue('sectionId', sectionId);
  }, [sectionId, setFieldValue]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await getSectionsByProjectId(projectId, { orderBy: 'name', order: 'ASC' });
        setSectionList(response?.data?.data?.rows);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          '"Oops! Something went wrong while fetching the section list. Please try again later."';
        toast.error(errorMessage);
      }
    };

    const fetchTaskClassificationOption = async () => {
      try {
        const response = await getTaskClassificationList({ projectId });
        setTaskClassificationOption(response?.data?.data?.rows);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          '"Oops! Something went wrong while fetching the task classification list. Please try again later."';
        toast.error(errorMessage);
      }
    };

    // Only fetch data if we have a projectId or if we're not in project selection mode
    if (projectId && (!showProjectSelection || selectedProjectId)) {
      fetchTaskClassificationOption();
      fetchEmployee();
      fetchSections();
      fetchAllProjectTasks();
    }

    if (action === 'update' && selectedTask) {
      handleUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, showProjectSelection, selectedProjectId]);

  useEffect(() => {
    fetchAllProjectTasks();
  }, [taskName]);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const Projects = await getProjectById(projectId);
        setProjectDetails(Projects?.data?.data);
      } catch (e) {
        toast.error(e.message);
      }
    };

    if (projectId) fetchProjectDetails();
  }, [projectId]);

  useEffect(() => {
    // Set tabindex for all elements within the Quill toolbar
    const toolbarElements = document.querySelectorAll('.ql-toolbar *');
    toolbarElements.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }, []);

  // const projectRole =
  //   projectDetails?.projectmember?.find((data) => data.userId === userId)?.projectRoles?.roleName ?? '';

  const projectRole = useMemo(
    () => projectDetails?.projectmember?.find((data) => data.userId === userId)?.projectRoles?.roleName ?? '',
    [projectDetails, userId]
  );

  // const consumedHrs = projectDetails?.projectmember?.reduce((sum, member) => sum + member?.totalTime / 3600, 0) ?? 0;

  const consumedHrs = useMemo(
    () => projectDetails?.projectmember?.reduce((sum, member) => sum + member?.totalTime / 3600, 0) ?? 0,
    [projectDetails]
  );

  const isTeamMemberRole = !isAdmin() && projectRole === 'Team Members';
  const canEditAssignee = isAdmin() || isStakeholder() || isEditPermission || projectRole !== 'Team Members';
  const isNonAdminNonStakeholder = !isAdmin() && !isStakeholder();
  const handlePlannedHourChange = (e) => {
    formik.setFieldTouched('plannedHours', false);
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue); // Check if input matches the pattern for valid decimal/float values
    if (isValidInput && inputValue >= 0) {
      // Check if input is valid and not negative
      const plannedHours = inputValue ? parseFloat(inputValue, 10) : 0;
      formik.setFieldValue('plannedHours', plannedHours);

      // Calculate the end date using the common function
      const endDate = calculateEndDate(formik.values.startDate, plannedHours);
      formik.setFieldValue('endDate', endDate);
    }

    e.stopPropagation();
  };

  useEffect(() => {
    if (isNonAdminNonStakeholder && action === 'create') setFieldValue('userId', accountData?.id);
  }, [isNonAdminNonStakeholder, action, accountData?.id, setFieldValue]);

  return (
    <>
      <Box>
        <Stack direction="row" sx={{ justifyContent: 'space-between', marginTop: '5px', alignItems: 'center' }}>
          <Typography
            variant="h5"
            align="center"
            sx={{
              m: '8px 0',
              color: '#999',
              fontWeight: '600',
              fontSize: { xs: '.9rem', sm: '1rem', md: '1.25rem' },
              paddingLeft: '24px',
            }}
          >
            {action === 'create' ? `Create a New ${isStakeholder()?'Issue':'Task'}` : action === 'update' ? `Edit a ${isStakeholder()?'Issue':'Task'}` : ''}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            {action === 'create' ? (
              <LoadingButton type="submit" fullWidth variant="contained" onClick={submitForm} loading={isSubmitting}>
                {isStakeholder() ? 'Add Issue' : 'Add Task'}
              </LoadingButton>
            ) : (
              <LoadingButton type="submit" fullWidth variant="contained" onClick={submitForm} loading={isSubmitting}>
                {isStakeholder()?'Update Issue':'Update Task'}
              </LoadingButton>
            )}
            {action === 'update' && !isStakeholder() && !isClient() && !selectedTask?.isParentTask ? (
              <Tooltip title="go to timetracker">
                <IconButton aria-label="expand row" sx={{ fontSize: ' xx-large' }} onClick={handleNavigate}>
                  <TimerTwoToneIcon fontSize=" xx-large" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title="back">
              <IconButton
                aria-label="expand row"
                size="small"
                // sx={{ marginRight: 'auto' }}
                onClick={handleClose}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit} style={{ marginTop: '.5rem' }}>
            <Stack direction="column" spacing={4} style={{ paddingRight: '28px' }}>
              <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid container item xs={12} columnSpacing={2}>
                  {/* Task Name (Left) */}
                  <Grid item xs={12}>
                    <FormControl className={classes.width}>
                      <TextField
                        autoFocus
                        size="small"
                        label={stakeholder ? 'Issue Name' : 'Task Name'}
                        placeholder={stakeholder ? 'Add issue name...' : 'Add task name'}
                        variant="outlined"
                        {...getFieldProps('taskName')}
                        fullWidth
                        error={touched.taskName && Boolean(errors.taskName)}
                        helperText={touched.taskName && errors.taskName}
                        onChange={(e) => {
                          setFieldValue('taskName', e.target.value);
                          e.stopPropagation();
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <FormControl className={classes.width}>
                    <TextField
                      size="small"
                      type="number"
                      label="Priority"
                      variant="outlined"
                      fullWidth
                      {...getFieldProps('priority')}
                      InputLabelProps={{ shrink: true }}
                      error={touched.priority && Boolean(errors.priority)}
                      helperText={touched.priority && errors.priority}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                          setFieldValue('priority', val ? parseInt(val, 10) : '');
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
                {!selectedTask?.parentTaskId && !values.parentTaskId && canAddParentTask && (
                  <Grid item xs={6}>
                    <FormControl className={classes.width}>
                      <Tooltip title="Is Parent">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formik.values.isParentTask}
                              onChange={(e) => setFieldValue('isParentTask', e.target.checked)}
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                              sx={{
                                color: 'grey.400',
                                '&.Mui-checked': {
                                  color: 'success.main',
                                },
                              }}
                            />
                          }
                          label="Parent Task?"
                          sx={{
                            ml: 0,
                            height: '40px', // aligns with TextField height
                          }}
                        />
                      </Tooltip>
                    </FormControl>
                  </Grid>
                )}
                {showProjectSelection && (
                  <Grid item xs={12}>
                    <FormControl
                      error={touched.projectId && Boolean(errors.projectId)}
                      sx={{ minWidth: 120 }}
                      fullWidth
                      className={classes.select}
                    >
                      <InputLabel>Project</InputLabel>
                      <Select
                        size="small"
                        name="projectId"
                        {...getFieldProps('projectId')}
                        label="Project"
                        value={values.projectId || ''}
                      >
                        {projects.map((project) => (
                          <MenuItem value={project.id} key={project.id}>
                            {project.projectName}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText error>{touched.projectId && errors.projectId}</FormHelperText>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} className={classes.quillGrid}>
                  <ReactQuill
                    theme="snow"
                    name="description"
                    onChange={(value) => {
                      setFieldValue('description', value);
                    }}
                    value={values?.description}
                    modules={modules}
                    formats={formats}
                    style={{
                      border: touched.description && errors.description ? '1px solid #FF4842' : undefined,
                      borderRadius: '6px',
                      fontFamily: '"Public Sans", sans-serif',
                      fontSize: '16px',
                    }}
                    placeholder={stakeholder ? 'Add issue description...' : 'Add task description...'}
                  />
                  {touched.description && errors.description && (
                    <div style={{ color: 'red', marginLeft: '14px', fontSize: '.75rem' }}>{errors.description}</div>
                  )}
                </Grid>

                {!hideProjectName && !showProjectSelection && (
                  <>
                    <StyledGrid item xs={6}>
                      <FormControl sx={{ minWidth: 120 }} fullWidth>
                        <TextField
                          size="small"
                          aria-readonly
                          label="Project name"
                          variant="outlined"
                          // value={location?.state?.name || project?.name || projectName}
                          value={project?.name || projectName}
                          disabled
                        />
                      </FormControl>
                    </StyledGrid>
                  </>
                )}

                {!stakeholder && (
                  <StyledGrid item xs={6}>
                    <FormControl
                      error={touched.sectionId && Boolean(errors.sectionId)}
                      sx={{ minWidth: 120 }}
                      fullWidth
                      className={classes.select}
                    >
                      <InputLabel>Section</InputLabel>
                      <Select
                        size="small"
                        name="sectionId"
                        {...getFieldProps('sectionId')}
                        label="Section"
                        disabled={showProjectSelection && !selectedProjectId}
                        onChange={(event, value) => {
                          setFieldValue('sectionId', value?.props?.value);
                        }}
                      >
                        {sectionList.map((section) => (
                          <MenuItem value={section.id} key={section.id}>
                            {section.name}
                          </MenuItem>
                        ))}
                        {!showProjectSelection && (
                          <MenuItem value="" key="add" onClick={hanldeAddSectionClick}>
                            Add New Section
                          </MenuItem>
                        )}
                      </Select>
                      <FormHelperText error>{touched.sectionId && errors.sectionId}</FormHelperText>
                    </FormControl>
                  </StyledGrid>
                )}
                {!stakeholder && (
                  <StyledGrid item xs={6}>
                    <FormControl
                      error={touched.classificationId && Boolean(errors.classificationId)}
                      sx={{ minWidth: 120 }}
                      fullWidth
                      className={classes.select}
                    >
                      <InputLabel>Task Classification Type</InputLabel>
                      <Select
                        size="small"
                        name="classificationId"
                        {...getFieldProps('classificationId')}
                        label="Task Classification Type"
                        onChange={(event, value) => {
                          setFieldValue('classificationId', value?.props?.value);
                        }}
                      >
                        {taskClassificationOption?.length > 0 ? (
                          taskClassificationOption.map((classification) => (
                            <MenuItem value={classification.id} key={classification.id}>
                              {classification.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>
                            No data available
                          </MenuItem>
                        )}
                      </Select>
                      <FormHelperText error>{touched.classificationId && errors.classificationId}</FormHelperText>
                    </FormControl>
                  </StyledGrid>
                )}

                {!stakeholder && (
                  <StyledGrid item xs={6}>
                    <FormControl sx={{ minWidth: 120 }} fullWidth className={classes.select}>
                      <InputLabel>Task Type</InputLabel>
                      <Select
                        size="small"
                        {...getFieldProps('taskType')}
                        error={touched.status && Boolean(errors.status)}
                        disabled={values.isParentTask}
                        onChange={(e) => {
                          const selectedTaskType = e.target.value;
                          setFieldValue('taskType', selectedTaskType);
                          // Check if the selected task type is "task" or "cr"
                          if (selectedTaskType === 'Task' || selectedTaskType === 'CR') {
                            setFieldValue('parentTaskId', null);
                          }
                        }}
                        label="Task Type"
                      >
                        <MenuItem value="Task">Task</MenuItem>
                        <MenuItem value="Issue" disabled={values.isParentTask}>
                          Issue{' '}
                        </MenuItem>
                        <MenuItem value="CR">Change Request</MenuItem>
                      </Select>
                      <FormHelperText error>{touched.status && errors.status} </FormHelperText>
                      {values.isParentTask && (
                        <FormHelperText sx={{ color: 'warning.main', fontSize: '0.75rem' }}>
                          Parent tasks cannot be Issue type
                        </FormHelperText>
                      )}
                    </FormControl>
                  </StyledGrid>
                )}

                {!stakeholder && !values.isParentTask && (
                  <Grid item xs={6}>
                    <FormControl className={classes.width}>
                      <Autocomplete
                        size="small"
                        variant="outlined"
                        fullWidth
                        options={parentTask}
                        getOptionLabel={(option) => option?.taskName || ''}
                        onInputChange={(event, newInputValue) => {
                          setTaskName(newInputValue);
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{
                              color: option.isParentTask ? '#2e2edbff' : 'inherit',
                              fontWeight: option.isParentTask ? 600 : 400,
                            }}
                          >
                            {option.taskName}
                          </li>
                        )}
                        value={parentTask.find((option) => option.id === formik.values.parentTaskId) || null}
                        filterOptions={(options, { inputValue }) => {
                          const inputValueLowerCase = inputValue.toLowerCase().trim();
                          return options.filter(
                            (option) =>
                              option?.id.toString().toLowerCase().includes(inputValueLowerCase) ||
                              option?.taskName.toLowerCase().includes(inputValueLowerCase)
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            size="small"
                            variant="outlined"
                            {...params}
                            label="Add Parent Task Name"
                            type="text"
                            error={
                              formik.values.taskType === 'Issue' &&
                              formik.touched.parentTaskId &&
                              Boolean(formik.errors.parentTaskId)
                            }
                            helperText={
                              formik.values.taskType === 'Issue' &&
                              formik.touched.parentTaskId &&
                              formik.errors.parentTaskId
                            }
                          />
                        )}
                        onChange={(event, selectedOption) => {
                          formik.setFieldValue(
                            'parentTaskId',
                            selectedOption?.id ? parseInt(selectedOption.id, 10) : null
                          );
                          formik.setFieldValue('parentTask', selectedOption || null);
                          formik.setFieldTouched('parentTaskId', false);
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}
                {getFieldProps('taskType').value === 'Issue' && (
                  <Grid item xs={6}>
                    <FormControl className={classes.width}>
                      <Autocomplete
                        size="small"
                        variant="outlined"
                        fullWidth
                        options={['High', 'Medium', 'Low']}
                        value={getFieldProps('issueType').value}
                        onChange={(event, newValue) => {
                          setFieldValue('issueType', newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Issue Priority"
                            variant="outlined"
                            error={touched.issueType && Boolean(errors.issueType)}
                            helperText={touched.issueType && errors.issueType}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                {!stakeholder && (
                  <StyledGrid item xs={6}>
                    <FormControl sx={{ minWidth: 120 }} fullWidth className={classes.select}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        size="small"
                        {...getFieldProps('status')}
                        error={touched.status && Boolean(errors.status)}
                        onChange={(e) => {
                          setFieldValue('status', e.target.value);
                        }}
                        label="status"
                      >
                        <MenuItem value="Not Started">Not Started</MenuItem>
                        <MenuItem value="In Progress">In Progress </MenuItem>
                        {action === 'update' && <MenuItem value="Completed">Completed</MenuItem>}
                        {action === 'update' && <MenuItem value="Blocked">Blocked</MenuItem>}
                        {action === 'update' && <MenuItem value="Reopened">Reopened</MenuItem>}
                      </Select>
                      <FormHelperText error>{touched.status && errors.status} </FormHelperText>
                    </FormControl>
                  </StyledGrid>
                )}

                {!stakeholder && !values.isParentTask && (
                  <Grid item xs={6}>
                    <FormControl
                      error={touched.userId && Boolean(errors.userId)}
                      sx={{ minWidth: 120 }}
                      fullWidth
                      className={classes.select}
                    >
                      <InputLabel>Assigned To</InputLabel>
                      <Select
                        size="small"
                        name="userId"
                        label="Assigned To"
                        {...getFieldProps('userId')}
                        onChange={(event, value) => {
                          setFieldValue('userId', value?.props?.value);
                        }}
                        disabled={
                          isStakeholder() ||
                          isClient() ||
                          (selectedTask?.consumedHours > 0 && action === 'update') ||
                          !canEditAssignee ||
                          (showProjectSelection && !selectedProjectId)
                        }
                      >
                        {!canEditAssignee ? (
                          <MenuItem value={accountData?.id}>{accountData?.userName}</MenuItem>
                        ) : (
                          employeeList.map((employee) => (
                            <MenuItem value={employee.id} key={employee.id}>
                              {employee.userName}
                            </MenuItem>
                          ))
                        )}
                        {canEditAssignee && !showProjectSelection && (
                          <MenuItem value="" key="add" onClick={hanldeAddMemberClick}>
                            Add New Member
                          </MenuItem>
                        )}
                      </Select>
                      <FormHelperText error>{touched.userId && errors.userId}</FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                {!stakeholder && (
                  <Grid item xs={6}>
                    <FormControl className={classes.width}>
                      <TextField
                        size="small"
                        value={formik.values.plannedHours || ''}
                        label="Planned Hours"
                        placeholder="Add planned hours"
                        variant="outlined"
                        type="number"
                        fullWidth
                        error={touched.plannedHours && Boolean(errors.plannedHours)}
                        helperText={touched.plannedHours && errors.plannedHours}
                        onWheel={(e) => e.target.blur()}
                        onChange={handlePlannedHourChange}
                        autoComplete="off"
                      />
                    </FormControl>
                  </Grid>
                )}

                {!stakeholder && (
                  <Grid item xs={6}>
                    <FormControl component="fieldset" className={classes.width}>
                      <RadioGroup
                        row
                        name="isBillable"
                        value={formik.values.isBillable}
                        onChange={formik.handleChange}
                        className={classes.radioSpacing}
                      >
                        <FormControlLabel value control={<Radio />} label="Billable" />
                        <FormControlLabel value={false} control={<Radio />} label="Non-billable" />
                      </RadioGroup>
                      {formik.touched.isBillable && formik.errors.isBillable && (
                        <FormHelperText error>{formik.errors.isBillable}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ pt: '0 !important' }} />

                {!stakeholder && (
                  <Grid item xs={6}>
                    <FormControl className={classes.width}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Paper>
                          <DatePicker
                            // disabled={!isTaskDateChangePermission}
                            label="Start Date"
                            value={formik.values.startDate}
                            inputFormat="DD MMM YYYY"
                            onChange={(selectedDate) => {
                              // const selectedDate = event.target.value;
                              formik.setFieldValue('startDate', selectedDate);
                            }}
                            minDate={new Date()}
                            renderInput={(params) => (
                              <TextField
                                size="small"
                                fullWidth
                                {...params}
                                {...getFieldProps('startDate')}
                                error={touched.startDate && Boolean(errors.startDate)}
                                helperText={touched.startDate && errors.startDate}
                              />
                            )}
                          />
                        </Paper>
                      </LocalizationProvider>
                    </FormControl>
                  </Grid>
                )}

                {!stakeholder && (
                  <Grid item xs={6}>
                    <FormControl className={classes.width}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Paper>
                          <DatePicker
                            // disabled={!isTaskDateChangePermission}
                            label="End Date"
                            value={formik.values.endDate}
                            inputFormat="DD MMM YYYY"
                            onChange={(selectedDate) => {
                              formik.setFieldValue('endDate', selectedDate);
                            }}
                            minDate={formik.values.startDate ? formik.values.startDate : new Date()}
                            renderInput={(params) => (
                              <TextField
                                size="small"
                                fullWidth
                                {...params}
                                {...getFieldProps('endDate')}
                                error={touched.endDate && Boolean(errors.endDate)}
                                helperText={touched.endDate && errors.endDate}
                              />
                            )}
                          />
                        </Paper>
                      </LocalizationProvider>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sx={{ pb: '10px' }}>
                  <AttachmentUploader attachments={attachments} setAttachments={setAttachments} sx={{ pt: '5px' }} />
                </Grid>
              </Grid>
            </Stack>
          </Form>
        </FormikProvider>
        {action === 'update' && selectedTask?.id && (
          <Box sx={{ mt: 4, px: 3, pb: 3, borderTop: '1px solid #e5e7eb', pt: 3 }}>
            <TaskComments
              taskId={selectedTask?.id}
              onCommentAdded={() => {
                // Optionally refresh task data
                if (onSubmit) onSubmit();
              }}
            />
          </Box>
        )}
        <SectionFormModal
          open={addSectionModal}
          onClose={handleCloseAddSection}
          projectId={projectId}
          onSubmit={onAddSection}
        />
        <AddMemberModal
          openModal={addMemberModal}
          onClose={handleCloseAddMember}
          action="create"
          consumedHrs={consumedHrs}
          id={projectId}
          projectDetails={projectDetails}
          onSubmit={onAddMember}
        />
      </Box>
    </>
  );
}

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline'],
    [{ size: [] }],
    [{ font: [] }],
    [{ align: ['right', 'center', 'justify'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: ['red', '#785412', 'black'] }],
  ],
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  '',
  'blockquote',
  'list',
  'bullet',
  'link',
  'color',
  'image',
  'background',
  'align',
  'size',
  'font',
];

const StyledGrid = styled(Grid)(
  () => `
  display:flex;
  align-items:center;
  `
);

const useStyles = makeStyles({
  addBtn: { margin: 'auto !important', width: '75%', marginTop: '2rem !important' },
  quillGrid: {
    '& .quill': {
      width: '100%',
    },
    '& .ql-container': {
      minHeight: '62px',
    },
    '& .ql-toolbar': {
      '& *': {
        tabIndex: -1,
      },
    },
    '& .ql-editor::before': {
      fontStyle: 'normal',
      fontFamily: '"Public Sans", sans-serif',
    },
  },
  select: {
    '& .MuiFormLabel-root': {
      top: '-6px',
    },
  },
  width: { width: '100%' },
  radioSpacing: {
    '& .MuiFormControlLabel-root': {
      marginRight: '32px', // 32px spacing
    },
  },
});

const GroupItems = styled('ul')({
  padding: 0,
});

export default ProjectAddTask;
